import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'

import ExpandLessRounded from '@mui/icons-material/ExpandLessRounded'
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded'
import StarRoundedIcon from '@mui/icons-material/StarRounded'
import { Box, Chip, Collapse, IconButton, TableCell, TableRow, Theme, Tooltip, Typography, alpha, useTheme } from '@mui/material'

import { GroupedTagComparison, TagStats, isHeadlinerGroup } from '../../helpers/creativeTagPerformance'
import { formatPerf } from '../../helpers/scoreDisplay'
import { useUAInsightsActions } from '../../hooks/useUAInsightsNavigation'
import { useUATaxonomy } from '../../hooks/useUATaxonomy'
import { GameMetadata, TagRef } from '../../types'

export const TOP_K = 3

interface CrownJewelsRowExpanderProps {
  dimension: string
  /** Source-game ranking, already filtered/sorted via `rankTagsForGame(_, 'src')`. */
  sourceRows: GroupedTagComparison[]
  /** Target-game ranking, already filtered/sorted via `rankTagsForGame(_, 'tgt')`. */
  targetRows: GroupedTagComparison[]
  sourceGame: GameMetadata
  targetGame: GameMetadata
  sourceColor: string
  targetColor: string
}

/**
 * One per-group row of the crown-jewel table. Default state shows the
 * group label + each game's top-{TOP_K} chip cells. Clicking the chevron
 * opens an expanded panel inside the source/target cells with the full
 * ranked tag lists for both games — keeping the chips column-aligned with
 * the trigger above them.
 */
export const CrownJewelsRowExpander: FC<CrownJewelsRowExpanderProps> = ({
  dimension,
  sourceRows,
  targetRows,
  sourceGame,
  targetGame,
  sourceColor,
  targetColor,
}) => {
  const { t } = useTranslation('ua-insights')
  const theme = useTheme()
  const { openTag } = useUAInsightsActions()
  const { dimensionLabel } = useUATaxonomy()
  const [expanded, setExpanded] = useState(false)

  const groupLabel = dimensionLabel(dimension)
  const isHeadliner = isHeadlinerGroup(dimension)
  const sourceTop = sourceRows.slice(0, TOP_K)
  const targetTop = targetRows.slice(0, TOP_K)
  // The expand control only appears when there is something below the
  // top-K cut on either side — otherwise the expanded panel would just
  // duplicate the row, which is annoying noise.
  const canExpand = sourceRows.length > TOP_K || targetRows.length > TOP_K

  const ariaLabel = expanded ? t('compare_crown_jewels_collapse_group', { group: groupLabel }) : t('compare_crown_jewels_expand_group', { group: groupLabel })

  return (
    <TableRow
      hover={canExpand}
      sx={{
        // When expanded, give the row a subtle highlight so the trigger
        // and its growing source/target cells read as a single unit.
        backgroundColor: expanded ? alpha(theme.palette.text.primary, 0.02) : undefined,
        // Apply a consistent 12px top inset to every cell in the row so
        // each tag group has a bit of breathing room above its content.
        '& > td': { paddingTop: '12px' },
      }}
    >
      <TableCell sx={{ verticalAlign: 'top' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 0.5 }}>
          {isHeadliner && <StarRoundedIcon sx={{ fontSize: 14, color: alpha(theme.palette.text.primary, 0.45) }} aria-hidden />}
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              color: 'text.secondary',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {groupLabel}
          </Typography>
        </Box>
      </TableCell>
      <TableCell sx={{ verticalAlign: 'top' }}>
        <ChipCell rows={sourceTop} side="src" accent={sourceColor} onChipClick={openTag} theme={theme} />
        {canExpand && (
          <Collapse in={expanded} unmountOnExit timeout="auto">
            <Box sx={{ pt: 1 }}>
              <FullRankingPanel rows={sourceRows} side="src" accent={sourceColor} gameName={sourceGame.name} onChipClick={openTag} theme={theme} />
            </Box>
          </Collapse>
        )}
      </TableCell>
      <TableCell sx={{ verticalAlign: 'top' }}>
        <ChipCell rows={targetTop} side="tgt" accent={targetColor} onChipClick={openTag} theme={theme} />
        {canExpand && (
          <Collapse in={expanded} unmountOnExit timeout="auto">
            <Box sx={{ pt: 1 }}>
              <FullRankingPanel rows={targetRows} side="tgt" accent={targetColor} gameName={targetGame.name} onChipClick={openTag} theme={theme} />
            </Box>
          </Collapse>
        )}
      </TableCell>
      <TableCell sx={{ width: 48, verticalAlign: 'top' }}>
        {canExpand ? (
          <Tooltip title={ariaLabel}>
            <IconButton size="small" onClick={() => setExpanded((prev) => !prev)} aria-label={ariaLabel} aria-expanded={expanded}>
              {expanded ? <ExpandLessRounded fontSize="small" /> : <ExpandMoreRounded fontSize="small" />}
            </IconButton>
          </Tooltip>
        ) : null}
      </TableCell>
    </TableRow>
  )
}

interface ChipCellProps {
  rows: GroupedTagComparison[]
  /** Which game's stats (`row.src` or `row.tgt`) the chips should display. */
  side: 'src' | 'tgt'
  accent: string
  onChipClick: (tagId: string) => void
  theme: Theme
}

const ChipCell: FC<ChipCellProps> = ({ rows, side, accent, onChipClick, theme }) => {
  if (rows.length === 0) {
    return (
      <Typography variant="caption" color="text.disabled" sx={{ py: 0.5, display: 'inline-block' }}>
        —
      </Typography>
    )
  }
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, py: 0.25 }}>
      {rows.map((row, index) => {
        const stats = row[side]
        if (!stats) return null
        return (
          <CrownTagChip key={row.tag.id} tag={row.tag} stats={stats} accent={accent} onClick={() => onChipClick(row.tag.id)} theme={theme} rank={index + 1} />
        )
      })}
    </Box>
  )
}

interface FullRankingPanelProps {
  rows: GroupedTagComparison[]
  /** Which game's stats to read off each comparison row. */
  side: 'src' | 'tgt'
  accent: string
  gameName: string
  onChipClick: (tagId: string) => void
  theme: Theme
}

const FullRankingPanel: FC<FullRankingPanelProps> = ({ rows, side, accent, gameName, onChipClick, theme }) => {
  const { t } = useTranslation('ua-insights')

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: accent, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {gameName}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t('compare_crown_jewels_full_ranking', { count: rows.length })}
        </Typography>
      </Box>
      {rows.length === 0 ? (
        <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
          {t('compare_crown_jewels_panel_empty', { name: gameName })}
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {rows.map((row, index) => {
            const stats = row[side]
            if (!stats) return null
            return (
              <CrownTagChip
                key={row.tag.id}
                tag={row.tag}
                stats={stats}
                accent={accent}
                onClick={() => onChipClick(row.tag.id)}
                theme={theme}
                rank={index + 1}
              />
            )
          })}
        </Box>
      )}
    </Box>
  )
}

interface CrownTagChipProps {
  tag: TagRef
  stats: TagStats
  accent: string
  onClick: () => void
  theme: Theme
  /** Optional 1-based rank shown as a small prefix in the expanded list. */
  rank?: number
}

/**
 * Compact `[#1 Tag Name · 78%]` chip used inside crown-jewel cells. The
 * trailing percentage is the tag's mean Ad Performance (same scale as
 * `CreativeAdsTable.col_ad_performance` — strongest ad on the network
 * anchors at 100). Each chip is clickable (opens the tag-details dialog)
 * and carries a tooltip with carrier count + share-of-ads for sanity-
 * checking.
 */
const CrownTagChip: FC<CrownTagChipProps> = ({ tag, stats, accent, onClick, theme, rank }) => {
  const { t } = useTranslation('ua-insights')
  const { tagLabel } = useUATaxonomy()
  const tooltip = t('compare_tag_readout_tooltip', {
    perf: formatPerf(stats.perf),
    carriers: stats.carrierCount,
    share: stats.share.toFixed(0),
  })
  const label = (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      {rank !== undefined && (
        <Typography
          variant="caption"
          sx={{
            fontSize: '0.62rem',
            fontWeight: 'bold',
            color: alpha(theme.palette.text.primary, 0.5),
            fontVariantNumeric: 'tabular-nums',
            lineHeight: 1.1,
          }}
        >
          {`#${rank}`}
        </Typography>
      )}
      <Typography variant="caption" sx={{ fontSize: '0.78rem', fontWeight: 'bold', color: theme.palette.text.primary, lineHeight: 1.2 }}>
        {tagLabel(tag)}
      </Typography>
      <Typography
        sx={{
          fontSize: '0.72rem',
          fontWeight: 700,
          color: accent,
          fontVariantNumeric: 'tabular-nums',
          lineHeight: 1.2,
        }}
      >
        {`${formatPerf(stats.perf)}%`}
      </Typography>
    </Box>
  )

  return (
    <Tooltip title={tooltip} arrow placement="top">
      <Chip
        size="small"
        clickable
        onClick={onClick}
        label={label}
        sx={{
          height: 24,
          backgroundColor: alpha(accent, 0.1),
          border: '1px solid',
          borderColor: alpha(accent, 0.3),
          '& .MuiChip-label': { px: 1, py: 0 },
          '&:hover': {
            backgroundColor: alpha(accent, 0.18),
            borderColor: alpha(accent, 0.5),
          },
        }}
      />
    </Tooltip>
  )
}
